/**
 * 项目管理API - 基于真实的项目工作流数据
 *
 * 功能：
 * - 获取项目工作流列表
 * - 获取项目详情
 * - 支持筛选、分页、排序
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';

// ==================== 类型定义 ====================

export interface ProjectWorkflow {
  _id: string;
  project_code: string;
  project_name: string;

  // 源问卷信息
  source_questionnaire_id: string;
  questionnaire_approved_at: string;

  // 发行人信息
  issuer_id: string;
  issuer_name: string;
  issuer_contact_name: string;
  issuer_contact_email: string;

  // 项目基础信息
  bond_amount: number;
  bond_term: string;
  green_project_type: string;
  ai_score: number;
  risk_level: string;

  // 流程状态
  current_stage: string;
  overall_status: string;
  progress_percentage: number;

  // 时间管理
  created_at: string;
  started_at: string;
  expected_completion: string;
  actual_completion?: string;
  updated_at: string;

  // 显示字段
  bond_amount_display: string;
  created_at_display: string;
  stage_display: string;
  status_display: string;

  // 增强字段
  current_stage_info?: {
    status: string;
    completion_percentage: number;
    started_at: string;
    expected_completion: string;
    assignee_role: string;
  };

  latest_approval?: {
    stage: string;
    decision: string;
    reviewed_at: string;
    approver_name: string;
  };

  participants_count: number;
}

export interface StageInfo {
  _id: string;
  project_code: string;
  stage: string;
  status: string;
  total_modules: number;
  completed_modules: number;
  completion_percentage: number;
  requires_approval: boolean;
  approval_status?: string;
  started_at: string;
  expected_completion: string;
  actual_completion?: string;
  assignee_role: string;

  // 显示字段
  stage_display: string;
  status_display: string;
  completion_percentage_display: string;
  expected_completion_display: string;
  duration_days: number;
}

export interface ApprovalRecord {
  _id: string;
  project_code: string;
  stage: string;
  decision?: string;
  comments: string;
  approver_name?: string;
  submitted_at: string;
  reviewed_at?: string;

  // 显示字段
  stage_display: string;
  decision_display?: string;
  reviewed_at_display?: string;
  submitted_at_display: string;
}

export interface ProjectParticipant {
  _id: string;
  project_code: string;
  participant_id: string;
  participant_name: string;
  participant_type: string;
  role: string;
  contact_name: string;
  contact_email: string;
  status: string;
  joined_at: string;

  // 显示字段
  participant_type_display: string;
  role_display: string;
  status_display: string;
  joined_at_display: string;
}

export interface DetailedAssessmentInfo {
  _id: string;
  project_code: string;
  total_modules: number;
  completed_modules: number;
  completion_percentage: number;
  status: string;

  // 显示字段
  completion_percentage_display: string;
  status_display: string;

  modules_summary: Array<{
    module_key: string;
    module_name: string;
    completed: boolean;
    completion_percentage: number;
    completion_percentage_display: string;
  }>;
}

export interface ProjectDetail extends ProjectWorkflow {
  // 当前阶段详情
  current_stage_details?: StageInfo;

  // 阶段历史
  stage_history: StageInfo[];

  // 审核记录
  approval_history: ApprovalRecord[];

  // 项目参与方
  participants: ProjectParticipant[];

  // 详细评估信息
  detailed_assessment?: DetailedAssessmentInfo;

  // 源问卷信息
  source_questionnaire?: {
    questionnaire_id: string;
    project_name: string;
    issuer_name: string;
    submitted_at: string;
    submitted_at_display: string;
    ai_score: number;
    risk_level: string;
    status: string;
  };

  // 统计信息
  statistics: {
    total_stages: number;
    completed_stages: number;
    total_approvals: number;
    approved_count: number;
    rejected_count: number;
    total_participants: number;
    active_participants: number;
  };
}

export interface ProjectListParams {
  page?: number;
  limit?: number;
  status?: string | string[];
  stage?: string | string[];
  issuer?: string;
  search?: string;
  sort_by?:
    | 'created_at'
    | 'updated_at'
    | 'project_name'
    | 'progress_percentage';
  sort_order?: 'asc' | 'desc';
}

export interface ProjectListResponse {
  success: boolean;
  data: {
    projects: ProjectWorkflow[];
    pagination: {
      current_page: number;
      total_pages: number;
      total_count: number;
      limit: number;
      has_next_page: boolean;
      has_prev_page: boolean;
    };
    filters: {
      status?: string | string[];
      stage?: string | string[];
      issuer?: string;
      search?: string;
    };
    sort: {
      sort_by: string;
      sort_order: string;
    };
  };
}

export interface ProjectDetailResponse {
  success: boolean;
  data: {
    project: ProjectDetail;
  };
}

// ==================== API Slice ====================

export const projectManagementApi = createApi({
  reducerPath: 'projectManagementApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['ProjectWorkflow', 'ProjectDetail'],
  endpoints: builder => ({
    // 获取项目工作流列表
    getProjectWorkflows: builder.query<ProjectListResponse, ProjectListParams>({
      query: params => ({
        url: '/project-management/projects',
        method: 'GET',
        params,
      }),
      providesTags: ['ProjectWorkflow'],
    }),

    // 获取项目工作流详情
    getProjectWorkflowDetail: builder.query<ProjectDetailResponse, string>({
      query: projectCode => ({
        url: `/project-management/projects/${projectCode}`,
        method: 'GET',
      }),
      providesTags: (result, error, projectCode) => [
        { type: 'ProjectDetail', id: projectCode },
      ],
    }),

    // 获取项目管理统计数据
    getProjectManagementStatistics: builder.query<
      {
        success: boolean;
        data: {
          total_projects: number;
          active_projects: number;
          completed_projects: number;
          today_projects: number;
          this_month_projects: number;
          average_progress: number;
          stage_distribution: Record<string, number>;
          status_distribution: Record<string, number>;
          monthly_trend: Array<{
            month: string;
            created: number;
            completed: number;
          }>;
          bond_amount_stats: {
            total_amount: number;
            average_amount: number;
            max_amount: number;
            min_amount: number;
            project_count: number;
          };
        };
      },
      void
    >({
      query: () => ({
        url: '/project-management/statistics',
        method: 'GET',
      }),
      providesTags: ['ProjectWorkflow'],
      // 缩短缓存时间，使统计数据更快失效
      keepUnusedDataFor: 0, // 不缓存，确保每次都获取最新数据
    }),
  }),
});

// 导出hooks
export const {
  useGetProjectWorkflowsQuery,
  useGetProjectWorkflowDetailQuery,
  useGetProjectManagementStatisticsQuery,
} = projectManagementApi;
